import { Card, CardContent } from '@ui/card.tsx';
import { Button } from '@ui/button.tsx';
import { FileUploader } from '@r/components/import/file-uploader.tsx';
import { CsvFile } from '@r/components/import/csv-file.tsx';
import { ImportErrorPage } from '@r/components/import/import-error-page.tsx';
import { AddPointDialog } from '@admin/components/forms/points/add-point-dialog.tsx';
import { unparse } from 'papaparse';
import { useFileParserWithErrors } from '@r/hooks/use-file-parser-patch-with-errors.ts';
import { useImportState } from '@r/hooks/use-import-state.ts';
import { pointsImportBody } from '@r/api/generated-zod/index.ts';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useWorkspaceName } from '@r/hooks/use-workspace-name.ts';
import { toast } from 'sonner';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { CoinsIcon, FilePlusIcon, Plus } from 'lucide-react';
import { useNavigate, useSearch } from '@tanstack/react-router';

type PointsImportSearchParams = {
  openAddPoint?: boolean | string;
  companyUniqueCode?: string;
  companyName?: string;
  [key: string]: unknown;
};

export default function PointsImport() {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as PointsImportSearchParams;
  const { sanitizedWorkspaceName } = useWorkspaceName();
  const { state, setErrorState, setPreviewState, resetState } = useImportState('points');
  const [isAddPointDialogOpen, setIsAddPointDialogOpen] = useState(false);
  const fixedCompany =
    typeof search.companyUniqueCode === 'string' &&
    search.companyUniqueCode.length > 0 &&
    typeof search.companyName === 'string' &&
    search.companyName.length > 0
      ? { id: 0, uniqueCode: search.companyUniqueCode, name: search.companyName }
      : undefined;

  useEffect(() => {
    const shouldOpenFromSearch = search.openAddPoint === true || search.openAddPoint === 'true';
    if (!shouldOpenFromSearch) return;
    setIsAddPointDialogOpen(true);
  }, [search.openAddPoint]);

  // Utiliser le schéma Zod généré par Orval pour les données d'import de points
  // Le schéma pointsImportBody a la structure { data: [...] }
  // Chaque item du tableau correspond à une ligne CSV
  const pointsImportSchema = pointsImportBody.shape.data.element;
  const { onParse } = useFileParserWithErrors(pointsImportSchema, '/admin/points/import');

  const csv = unparse({
    fields: [
      'userUniqueCode',
      'companyUniqueCode',
      'amount',
      'label',
      'typeLabel',
      'valueDate',
      'expirationDate'
    ],
    data: [
      ['UC01', 'CC01', 100, 'Label Point 1', 'CORE', '01/01/2021', '31/12/2026'],
      ['UC02', 'CC02', 200, 'Label Point 2', 'IMPORTED', null, '31/12/2026'],
      ['UC03', 'CC03', 300, 'Label Point 3', 'SPENT', '30/02/2024', null],
      ['UC04', 'CC04', 500, 'Label Point 4', 'EXPIRED', null, null],
      ['UC05', 'CC05', 1000, 'Label Point 5', 'CANCELED', '15/12/2023', null]
    ]
  });

  const filename = sanitizedWorkspaceName
    ? `${sanitizedWorkspaceName}_points_import_example.csv`
    : 'points_import_example.csv';

  /** Analyse le CSV déposé et dirige l'utilisateur vers le résultat ou la correction des erreurs. */
  const handleParse = async (files: File[]) => {
    const toastId = toast.loading(t('import.process.analyzing'));

    try {
      const result = await onParse(files);

      if (result.isPreview) {
        toast.dismiss(toastId);
        setPreviewState(result.errors ?? [], files[0], result.analysis ?? null);
      } else if (result.success) {
        toast.success(result.message || t('import.process.success'), { id: toastId });
        resetState();
      } else if (result.errors && result.errors.length > 0) {
        toast.dismiss(toastId);
        setErrorState(result.errors, files[0]);
      }
    } catch (error) {
      showOrvalErrorToast(error, typeof error === 'string' ? error : t('import.process.error'), {
        id: toastId
      });
    }
  };

  /** Relance l'import en excluant les lignes précédemment identifiées comme invalides. */
  const handleRetry = async () => {
    if (state.originalFile) {
      const toastId = toast.loading(t('import.process.loading'));

      try {
        const result = await onParse([state.originalFile], true);

        if (result.success) {
          toast.success(result.message || t('import.process.success'), { id: toastId });
          resetState();
        } else {
          toast.error(result.message || t('import.process.error'), { id: toastId });
          if (result.errors && result.errors.length > 0) {
            setErrorState(result.errors, state.originalFile);
          }
        }
      } catch (error) {
        showOrvalErrorToast(error, typeof error === 'string' ? error : t('import.process.error'), {
          id: toastId
        });
      }
    }
  };

  if (state.isPreview && state.originalFile) {
    return (
      <ImportErrorPage
        errors={state.errors}
        originalFile={state.originalFile}
        onRetry={handleRetry}
        onNewFile={resetState}
        importType="points"
        analysis={state.analysis}
      />
    );
  }

  // Page d'import normale
  return (
    <div className="space-y-10">
      <h1 className="text-4xl font-bold">{t('common.add.point')}</h1>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex gap-2 items-center">
            <CoinsIcon className="w-5 h-5" />
            <h2 className="text-xl font-semibold">{t('common.add.point')}</h2>
          </div>
          <p className="text-muted-foreground">{t('admin.points.add.block.description')}</p>
          <Button
            rounded="full"
            onClick={() => setIsAddPointDialogOpen(true)}
            className="bg-primary hover:bg-primary/90"
          >
            <Plus className="mr-2 w-4 h-4" />
            {t('common.add.point')}
          </Button>
        </CardContent>
      </Card>

      <AddPointDialog
        isOpen={isAddPointDialogOpen}
        onOpenChange={(open) => {
          setIsAddPointDialogOpen(open);
          if (!open) {
            navigate({
              to: '/points/import',
              search: (prev) => ({
                ...prev,
                openAddPoint: undefined,
                companyUniqueCode: undefined,
                companyName: undefined
              }),
              replace: true
            });
          }
        }}
        fixedCompany={fixedCompany}
      />

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex gap-2 items-center">
            <FilePlusIcon className="w-5 h-5" />
            <h2 className="text-xl font-semibold">{t('admin.points.import.block.title')}</h2>
          </div>
          <p className="text-muted-foreground">{t('admin.points.import.block.description')}</p>
          <p className="text-sm text-muted-foreground">{t('admin.points.import.block.example')}</p>
          <CsvFile csv={csv} filename={filename} label={t('common.csv.example.button')} />

          <FileUploader
            accept={{
              'text/csv': [],
              'application/vnd.ms-excel': [],
              'application/csv': [],
              'text/x-csv': [],
              'application/x-csv': [],
              'text/comma-separated-values': [],
              'text/x-comma-separated-values': []
            }}
            onParse={handleParse}
          />
        </CardContent>
      </Card>
    </div>
  );
}
